import type { Configs, DateValueType, ShortcutsItem } from "../types";

import DEFAULT_SHORTCUTS from "../constants/shortcuts";
import { dateIsSame } from "../libs/date";

const getShortcutDisplayText = (
  key: string,
  shortcut: ShortcutsItem,
  configs?: Configs,
): string => {
  if (!configs?.shortcuts || !(key in configs.shortcuts)) {
    return shortcut.text;
  }

  if (key === "past" && shortcut.daysNumber) {
    const pastShortcut = configs.shortcuts.past;

    return typeof pastShortcut === "function"
      ? pastShortcut(shortcut.daysNumber)
      : shortcut.text;
  }

  const configuredShortcut =
    configs.shortcuts[key as keyof typeof configs.shortcuts];

  return typeof configuredShortcut === "string"
    ? configuredShortcut
    : shortcut.text;
};

/**
 * Collapses a selected range down to the name of the shortcut it matches — a
 * range of exactly the last 30 days reads as "Last 30 days" rather than
 * "01 Jul 2025 - 31 Jul 2025". Returns `undefined` for a custom range, so
 * callers fall back to the formatted dates.
 *
 * Shared by every "field"-style date-range trigger (Performance report, Shared
 * rebate programs) so the collapsing rule stays identical across reports.
 */
export const getShortcutButtonLabel = (
  value: DateValueType,
  configs?: Configs,
): string | undefined => {
  const startDate = value?.startDate;
  const endDate = value?.endDate;

  if (!startDate || !endDate) {
    return undefined;
  }

  const shortcutEntries = !configs?.shortcuts
    ? Object.entries(DEFAULT_SHORTCUTS)
    : Object.entries(configs.shortcuts).flatMap(([key, configuredShortcut]) => {
        if (Object.prototype.hasOwnProperty.call(DEFAULT_SHORTCUTS, key)) {
          return [[key, DEFAULT_SHORTCUTS[key]] as const];
        }

        if (
          typeof configuredShortcut !== "object" ||
          !configuredShortcut ||
          !("text" in configuredShortcut) ||
          !("period" in configuredShortcut)
        ) {
          return [];
        }

        return [[key, configuredShortcut] as const];
      });

  for (const [key, shortcutOrList] of shortcutEntries) {
    const shortcuts = Array.isArray(shortcutOrList)
      ? shortcutOrList
      : [shortcutOrList];

    for (const shortcut of shortcuts) {
      if (
        dateIsSame(startDate, shortcut.period.start, "date") &&
        dateIsSame(endDate, shortcut.period.end, "date")
      ) {
        return getShortcutDisplayText(key, shortcut, configs);
      }
    }
  }

  return undefined;
};
